import type { CSSProperties } from 'react';

import { cn } from '@april9au/react-common/utils';

import type { OrderingSlice } from '@/chat/activity';
import { orderSteps, shuffledSteps } from '@/chat/activity';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { answerKey } from '@/lib/autopilot';

/**
 * Put the steps in order. Tapping a step stamps it with the next number;
 * tapping a numbered step clears it and everything after it, so a mistake
 * costs one tap rather than a full Reset.
 *
 * The steps are buttons in a list, so the keyboard needs nothing extra.
 *
 * `answerKey` carries each step's positional id — that is, the answer — for the
 * non-production Autopilot, and is compiled out of a production build.
 */

export type OrderingSurfaceProps = {
  slice: OrderingSlice;
  sequence: Array<string>;
  armed: boolean;
  revealed: boolean;
  /** Calls out the first step. */
  hint: boolean;
  onChange: (sequence: Array<string>) => void;
};

export function OrderingSurface({ slice, sequence, armed, revealed, hint, onChange }: OrderingSurfaceProps) {
  const { chat } = useStrings();
  // Revealed, the list *is* the answer: the authored order, numbered.
  const steps = revealed ? orderSteps(slice) : shuffledSteps(slice);
  const first = hint && armed ? orderSteps(slice)[0]?.id : undefined;
  const last = sequence.at(-1);
  // A frozen card that was not revealed is one the learner got right, so the
  // numbers turn green. While armed they stay blue: the card never marks
  // itself before Check.
  const right = !armed && !revealed;

  const toggle = (id: string) => {
    const position = sequence.indexOf(id);

    onChange(position === -1 ? [...sequence, id] : sequence.slice(0, position));
  };

  return (
    <>
      {revealed ? (
        <p className="text-chat-ink-muted mb-2 text-[11px] font-bold tracking-[0.1em] uppercase">
          {chat.chat_activity_answer_order}
        </p>
      ) : null}

      <ol className="flex flex-col gap-2">
        {steps.map((step, index) => {
          const position = revealed ? index : sequence.indexOf(step.id);
          const picked = position !== -1;

          return (
            <li key={step.id}>
              <button
                type="button"
                data-autopilot="step"
                {...answerKey(step.id)}
                disabled={!armed}
                aria-pressed={picked}
                onClick={() => toggle(step.id)}
                style={{ '--chip-index': index } as CSSProperties}
                className={cn(
                  'chat-chip-in chat-pressable flex min-h-11 w-full items-center gap-3 rounded-xl border px-3 py-2',
                  'text-start text-[15px] font-semibold focus-visible:ring-[3px] focus-visible:outline-none',
                  'focus-visible:ring-surf-blue/40',
                  picked
                    ? 'border-surf-blue bg-surf-blue/8 text-chat-ink'
                    : 'border-chat-hairline bg-bubble-buddy text-chat-ink hover:border-surf-blue',
                  (revealed || (right && picked)) && 'border-answer-correct bg-answer-correct/8',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'grid size-7 shrink-0 place-items-center rounded-full text-[13px] font-bold',
                    picked
                      ? 'bg-surf-blue text-white'
                      : 'border-chat-hairline text-chat-ink-muted border border-dashed',
                    (revealed || (right && picked)) && 'bg-answer-correct text-white',
                    // The number lands with the tap; nothing else moves.
                    picked && 'activity-number-in',
                  )}
                >
                  {picked ? position + 1 : ''}
                </span>
                <span className="flex-1">{step.label}</span>
                {step.id === first && !picked ? (
                  <span className="activity-hint-in bg-surf-yellow text-surf-dark-blue shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold tracking-[0.08em] uppercase">
                    {chat.chat_activity_hint_first}
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ol>

      <p aria-live="polite" className="sr-only">
        {last === undefined
          ? chat.chat_activity_steps_cleared
          : formatString(chat.chat_activity_step_picked, {
              current: sequence.length,
              label: steps.find((step) => step.id === last)?.label ?? '',
            })}
      </p>
    </>
  );
}
